<template>
  <div>
    <div v-if="!loading">
      <v-row>
        <v-col cols="12" md="6"
        >
          <v-btn
            min-width="100%"
            outlined
            @click="close()"
            class="mr-2 mb-1"
          >Continue Browsing
          </v-btn
          >
        </v-col
        >
        <v-col cols="12" md="6">
          <v-btn
            color="primary"
            class="mb-1 lighten-1"
            @click="executeTrade"
            min-width="100%"
            :disabled="Boolean(errorMessage)"
          ><span v-if="limitOrder">Place Limit Order</span
          ><span v-else>Execute Trade</span></v-btn
          >
        </v-col>
      </v-row>
      <v-alert v-if="errorMessage" color="red" dark class="mt-2">
        <v-row align="center">
          <v-col class="grow">
            <span v-html="errorMessage"></span>
          </v-col>
          <v-col class="shrink">
            <v-btn @click="init()">Retry</v-btn>
          </v-col>
        </v-row>
      </v-alert>
      <terms :buy-sell="buySell" :limit-order="limitOrder"></terms>
    </div>
    <v-progress-linear
      class="mt-4 mb-4"
      indeterminate
      v-else
    ></v-progress-linear>
  </div>
</template>

<script>
import Terms from '@/components/Terms'
import {mapGetters} from 'vuex'
import {get} from 'lodash'

export default {
  props: ['buySell', 'companyId', 'limitOrder', 'error', 'total'],
  data() {
    return {
      loading: true,
      errorMessage: null
    }
  },
  watch: {
    companyId: {
      handler() {
        this.init()
      },
    },
  },
  mounted() {
    this.init()
  },
  components: {
    Terms,
  },
  computed: {
    ...mapGetters(['getConfig', 'getDefaultBillingAddress', 'getSelectedCompany']),
    selectedBillingAddress: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.selectedBillingAddress`
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.selectedBillingAddress`,
          value,
        })
      },
    },
  },
  methods: {
    async init() {
      this.loading = true
      if (!get(this, `getDefaultBillingAddress.id`)) {
        await Promise.all([
          await this.$store.dispatch('refreshSelectedCompany'),
          await this.$store.dispatch('fetchConfig'),
        ])
      }
      if (!get(this, `selectedBillingAddress.id`))
        this.selectedBillingAddress = this.getDefaultBillingAddress
      // Errors that prevent trade execution
      this.errorMessage = null
      // 1. Custom error passed in component
      if (this.error)
        this.errorMessage = this.error
      const tradingEnabled = this.getConfig(`TRADING_ENABLED`) !== false
      const buyTicketEnabled = this.getConfig(`BUY_TICKET_ENABLED`) !== false
      const sellTicketEnabled = this.getConfig(`SELL_TICKET_ENABLED`) !== false
      // 2. Trading is disabled (master switch)
      if (!tradingEnabled)
        this.errorMessage = `Trading is temporarily disabled. You may still add items to your trade ticket and execute the trade when trading is back online.`
      // 2a. Buy ticket is disabled
      if (this.buySell === 'buy' && !buyTicketEnabled)
        this.errorMessage = `Buying is temporarily disabled. You may still add items to your trade ticket and execute the trade when buying is back online.`
      // 2b. Sell ticket is disabled
      if (this.buySell === 'sell' && !sellTicketEnabled)
        this.errorMessage = `Selling is temporarily disabled. You may still add items to your trade ticket and execute the trade when selling is back online.`
      // 3. No default billing address on account
      if (!this.selectedBillingAddress || this.selectedBillingAddress.id === null)
        this.errorMessage = `We do not have a default billing address configured on your account. Please contact the trading desk to update your information.`
      // 4. This order would place customer over exposure limit
      if (this.getSelectedCompany.exposureLimit > 0) {
        const projectedExposureOverLimit = parseFloat(this.getSelectedCompany.presentExposure) + parseFloat(this.total) - parseFloat(this.getSelectedCompany.exposureLimit)
        if (projectedExposureOverLimit > 0)
          this.errorMessage = `<p>Unable to execute this order because it exceeds the trading exposure limit of ${this.$options.filters.toCurrency(this.getSelectedCompany.exposureLimit)} set for this account. Your current open trading exposure for this account is ${this.$options.filters.toCurrency(this.getSelectedCompany.presentExposure)}.</p>
            <p>Please call the trading desk to place the trade or discuss raising the exposure limit set on the account.</p>
            <p>Trading exposure is calculated by the total amount of unpaid sales orders and unreceived purchase orders.</p>`
      }
      // 5. Order exceeds configured buy/sell limit threshold (pre-submission guard; backend also validates)
      if (!this.errorMessage) {
        const configKey = this.buySell === 'buy' ? `BUY_LIMIT_THRESHOLD` : `SELL_LIMIT_THRESHOLD`
        const limitThreshold = this.getConfig(configKey)
        if (limitThreshold !== null && parseFloat(this.total) > parseFloat(limitThreshold)) {
          this.errorMessage = `<p><strong>Trade exceeds the online ${this.buySell} limit of ${this.$options.filters.toCurrency(limitThreshold)}.</strong></p>
            <p>Please split your order into multiple smaller trades or contact the trading desk for assistance.</p>`
        }
      }
      this.$emit('loaded')
      this.loading = false
    },
    close() {
      this.$emit('close')
    },
    executeTrade() {
      this.$emit('executeTrade')
    },
  },
}
</script>
